import React from 'react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';

type ButtonVariant = 'outline' | 'primary' | 'text';
type ButtonSize = 'sm' | 'md';

const buttonVariants: Record<ButtonVariant, string> = {
	outline:
		'border border-[#D9D9D9] bg-white text-gray-800 hover:bg-gray-50 hover:border-gray-400 hover:shadow-sm active:scale-[0.98]',
	primary:
		'bg-gradient-to-r from-[var(--colour-brand-primary)] to-[var(--colour-brand-primary-2)] text-white hover:opacity-90 active:scale-[0.98] hover:text-white',
	text: 'bg-transparent text-gray-500 hover:text-gray-900 hover:bg-gray-100/50 active:scale-[0.98]',
};

const buttonSizes: Record<ButtonSize, string> = {
	sm: 'px-3 h-[40px]',
	md: 'px-8 h-[40px] sm:px-12 sm:h-[60px]',
};

interface BaseProps {
	variant?: ButtonVariant;
	size?: ButtonSize;
}

interface ButtonProps extends BaseProps, React.ButtonHTMLAttributes<HTMLButtonElement> {
	asLink?: false;
	to?: never;
}

interface LinkProps extends BaseProps, React.AnchorHTMLAttributes<HTMLAnchorElement> {
	asLink: true;
	to: string;
}

type Props = ButtonProps | LinkProps;

export default function Button(props: Props) {
	const { variant = 'primary', size = 'md', className = '', children, asLink, to, ...rest } = props;

	const commonClasses = classNames(
		'inline-flex items-center justify-center rounded-md font-medium transition-all gap-2 no-underline',
		buttonVariants[variant],
		buttonSizes[size],
		className,
	);

	if (asLink) {
		return (
			<Link to={to as string} className={commonClasses} {...(rest as any)}>
				{children || 'Button'}
			</Link>
		);
	}

	return (
		<button className={commonClasses} {...(rest as any)}>
			{children || 'Button'}
		</button>
	);
}
